'use client'

import { Globe } from 'lucide-react'
import { useLanguage } from '@/lib/language-context'
import { cn } from '@/lib/utils'

const languages = [
  { code: 'en' as const, label: 'English', short: 'EN' },
  { code: 'bm' as const, label: 'Bahasa Malaysia', short: 'BM' }, // cspell:ignore Bahasa
  { code: 'zh' as const, label: '中文', short: 'ZH' },
]

interface LanguageSwitcherProps {
  className?: string
  variant?: 'button' | 'dropdown'
}

export function LanguageSwitcher({
  className,
  variant = 'button',
}: LanguageSwitcherProps) {
  const { language, setLanguage } = useLanguage()

  if (variant === 'button') {
    // Cycle: EN → BM → ZH → EN
    const nextLanguage = () => {
      const idx = languages.findIndex(l => l.code === language)
      const next = languages[(idx + 1) % languages.length]
      setLanguage(next.code)
    }

    const current = languages.find(l => l.code === language) ?? languages[0]

    return (
      <button
        type="button"
        onClick={nextLanguage}
        className={cn(
          'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors',
          'text-gray-600 hover:text-primary-green hover:bg-bg-light-green',
          'focus:outline-none focus:ring-2 focus:ring-primary-green focus:ring-offset-2',
          className
        )}
        aria-label={`Switch to ${languages[(languages.findIndex(l => l.code === language) + 1) % languages.length].label}`}
      >
        <Globe className="w-4 h-4" />
        <span className="font-semibold">{current.short}</span>
      </button>
    )
  }

  return (
    <div className={cn('relative group', className)}>
      <button
        type="button"
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-green focus:ring-offset-2"
        aria-label={`Current language: ${languages.find(l => l.code === language)?.label}`}
      >
        <Globe className="w-4 h-4" />
        <span>{languages.find(l => l.code === language)?.label}</span>
        <svg
          className="w-4 h-4 ml-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-label="Dropdown icon for language selection"
        >
          <title>Dropdown icon for language selection</title>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
        {languages.map((l, i) => (
          <button
            key={l.code}
            type="button"
            onClick={() => setLanguage(l.code)}
            className={cn(
              'w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100',
              i === 0 && 'rounded-t-md',
              i === languages.length - 1 && 'rounded-b-md'
            )}
            disabled={language === l.code}
          >
            {l.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// Language indicator component for showing current language
export function LanguageIndicator({ className }: { className?: string }) {
  const { language } = useLanguage()
  const current = languages.find(l => l.code === language) ?? languages[0]

  return (
    <div
      className={cn('flex items-center gap-2 text-sm text-gray-600', className)}
    >
      <Globe className="w-4 h-4" />
      <span className="font-medium">{current.label}</span>
    </div>
  )
}

// Compact language toggle for mobile — cycles through all 3
export function MobileLanguageToggle({ className }: { className?: string }) {
  const { language, setLanguage } = useLanguage()

  const nextLanguage = () => {
    const idx = languages.findIndex(l => l.code === language)
    const next = languages[(idx + 1) % languages.length]
    setLanguage(next.code)
  }

  const current = languages.find(l => l.code === language) ?? languages[0]

  return (
    <button
      type="button"
      onClick={nextLanguage}
      className={cn(
        'inline-flex items-center justify-center w-10 h-10 rounded-full',
        'bg-gray-100 text-gray-600 hover:bg-primary-green hover:text-white',
        'transition-colors duration-200',
        'focus:outline-none focus:ring-2 focus:ring-primary-green focus:ring-offset-2',
        className
      )}
      aria-label={`Switch to ${languages[(languages.findIndex(l => l.code === language) + 1) % languages.length].label}`}
    >
      <span className="text-sm font-bold">{current.short}</span>
    </button>
  )
}
